import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, Eye, EyeOff, UserPlus, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import LogoLockup from '../../components/shared/LogoLockup';

const SetupAccountPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setupAccount, isAuthenticated, user } = useAuth();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already authenticated after successful setup
  useEffect(() => {
    if (isAuthenticated && user) {
      const path = user.registered === false ? '/kyc' : user.role === 'admin' ? '/admin/users' : '/dashboard';
      navigate(path, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Password requirements
  const requirements = [
    { test: (p) => p.length >= 12, label: 'At least 12 characters' },
    { test: (p) => /[A-Z]/.test(p), label: 'One uppercase letter' },
    { test: (p) => /[a-z]/.test(p), label: 'One lowercase letter' },
    { test: (p) => /\d/.test(p), label: 'One number' },
    { test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p), label: 'One special character' },
  ];

  const isPasswordValid = requirements.every((req) => req.test(password));
  const passwordsMatch = password === confirmPassword && password.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!isPasswordValid) {
      setError('Please meet all password requirements');
      return;
    }
    
    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    const result = await setupAccount(token, password);
    
    if (!result.success) {
      setError(result.error);
      setIsLoading(false);
    }
    // If success, useEffect will handle the redirect
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <LogoLockup size="lg" />
          </Link>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-burgundy-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <UserPlus className="h-8 w-8 text-burgundy-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Complete Your Account Setup</h2>
            <p className="text-sm text-gray-500 mt-2">
              Welcome to Magnolia! Create a secure password to activate your account.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm border border-red-100 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div>
                  <p>{error}</p>
                  {error.includes('expired') && (
                    <p className="mt-2 text-xs">
                      Please contact your administrator to request a new setup link.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700">Create Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a secure password"
                  required
                  className="h-12 rounded-xl border-gray-200 focus:border-burgundy-500 focus:ring-burgundy-500 pr-12"
                  data-testid="setup-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <p className="text-xs font-medium text-gray-600 mb-2">Password Requirements:</p>
              {requirements.map((req, index) => (
                <div 
                  key={index} 
                  className={`flex items-center gap-2 text-xs ${
                    req.test(password) ? 'text-green-600' : 'text-gray-400'
                  }`}
                >
                  <CheckCircle className="h-3.5 w-3.5" />
                  {req.label}
                </div>
              ))}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-gray-700">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                className={`h-12 rounded-xl border-gray-200 focus:border-burgundy-500 focus:ring-burgundy-500 ${
                  confirmPassword && !passwordsMatch ? 'border-red-300' : ''
                }`}
                data-testid="setup-confirm-password-input"
              />
              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
              {confirmPassword && passwordsMatch && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-3.5 w-3.5" />
                  Passwords match
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isLoading || !isPasswordValid || !passwordsMatch}
              className="w-full h-12 rounded-xl bg-burgundy-600 hover:bg-burgundy-700 text-white font-medium disabled:opacity-50"
              data-testid="setup-submit-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Setting up your account...
                </>
              ) : (
                <>
                  Complete Setup
                  <ArrowRight className="h-5 w-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          <p className="text-xs text-gray-400 text-center mt-6">
            By completing setup, you agree to Magnolia's Terms of Service and Privacy Policy.
          </p>
        </div>

        <p className="text-center text-xs text-gray-400 mt-8">
          Magnolia Care - Because no parent should be forgotten.
        </p>
      </div>
    </div>
  );
};

export default SetupAccountPage;
